import React, { useEffect, useState } from "react";
import { IconArrowLeft, IconPlus } from "@velaro/velaro-component-lib/";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import { useLocation, useNavigate } from "react-router-dom";
import SecurityLogo from "@velaro/velaro-component-lib/dist/images/velaro_security-safe-encryption.png";
import Form1Logo from "@velaro/velaro-component-lib/dist/images/velaro_secure-form 1.svg";
import ShopifyLogo from "@velaro/velaro-component-lib/dist/images/shopify.png";
import VelaroLogo from "@velaro/velaro-component-lib/dist/images/velaro.svg";
import ArrowsLeftRight from "@velaro/velaro-component-lib/dist/images/arrows-left-right 1.svg";
import ShopifyPanel from "./ShopifyPanel";

interface Props {
  appStatus?: string;
}

export default function ShopifyCreate(props: Props) {
  const [storeName, setStoreName] = useState("");
  const [appClientId, setappClientId] = useState("");
  const [appSecret, setappSecret] = useState("");
  const [domain, setDomain] = useState("");

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const [configId, setConfigId] = useState<number>(-1);
  const [step, setStep] = useState(1);
  const [panels, setPanels] = useState<{ id: number; scopeType: string }[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const api = useApi();
  const location = useLocation();
  const { teamId, appStatus } = location.state || {};
  const navigate = useNavigate();
  const query = useQuery();

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  useEffect(() => {
    const shop = query.get("shop");
    if (shop) {
      console.log("Redirected from Shopify OAuth for shop:", shop);
      setIsConnecting(false);
      setSending(false);
      setStep(3);
    }
  }, [location.search]);

  const addPanel = () =>
    setPanels([...panels, { id: panels.length + 1, scopeType: "" }]);

  useEffect(() => {
    if (step === 3 && panels.length === 0) {
      addPanel();
    }
  }, [step]);

  async function handleSubmit() {
    setSending(true);
    try {
      const response = await api.messaging.post(
        "Shopify/Auth/SaveCredentials",
        {
          storeName,
          appClientId,
          appSecret,
          domain,
          teamId
        }
      );

      let result: any = {};
      if (response.ok) {
        result = await response.json();
      } else {
        console.error("Failed to save credentials:", response.status);
      }

      setSuccess(response.ok);
      if (response.ok && result.id) {
        setConfigId(result.id);
        setStep(step + 1);
      }
    } catch (err) {
      console.error("Error parsing response:", err);
      setSuccess(false);
    } finally {
      setSending(false);
    }
  }

  async function handleGrant() {
    if (!configId) {
      console.error("Config ID not set. Did you run handleSubmit first?");
      return;
    }

    setSending(true);
    try {
      const response = await api.messaging.get(`Shopify/authorize/${configId}`);
      const result = await response.json();
      setSuccess(response.ok);

      if (response.ok && result.authorizationUrl) {
        // Show loading animation while connecting to Shopify
        setIsConnecting(true);
        // Redirect user to Shopify authorization page
        window.location.href = result.authorizationUrl;
      }
    } catch (err) {
      console.error("Grant access failed", err);
      setSuccess(false);
    } finally {
      setSending(false);
    }
  }
  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const isNextActive = storeName && appClientId && appSecret && domain;

  const nextBtnClass = !isNextActive
    ? "bg-slate-300 border-slate-300 hover:bg-slate-300 hover:border-slate-500 text-white cursor-not-allowed"
    : "";

  return (
    <div className="panel bg-white rounded-2xl shadow-md mb-4 p-6 xl:mx-auto">
      {/* <div className="w-full justify-between items-center inline-flex">
        <div className="w-full rounded-lg items-center gap-2 flex">
          <div className="text-center text-slate-500 text-sm font-medium flex">
            {step > 1 ? (
              <button
                className="btn flex items-center gap-2"
                onClick={() => setStep(step - 1)}
              >
                {React.createElement(IconArrowLeft)}Back
              </button>
            ) : (
              <button
                className="btn flex items-center gap-2"
                onClick={() => navigate("/integrations")}
              >
                {React.createElement(IconArrowLeft)}Back
              </button>
            )}
          </div>
        </div>
      </div> */}

      <div className="flex items-center w-full mt-4">
        {[
          "Authentication",
          "Permission Request",
          "Connection Success",
          "Integration Settings",
          "Integration Complete"
        ].map((label, index) => {
          const isActive = step >= index + 1;
          const isCurrent = step === index + 1;
          const isCompleted = step > index + 1;
          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full border-2 z-10 ${
                    isActive ? "border-cornflower-blue-500" : "border-gray-300"
                  } bg-white`}
                >
                  <div
                    className={`w-6 h-6 rounded-full z-10 ${
                      isActive ? "bg-cornflower-blue-500" : "bg-gray-300"
                    }`}
                  ></div>
                </div>
                <p
                  className={`mt-2 font-medium text-sm ${
                    isCurrent
                      ? "text-cornflower-blue-500"
                      : isCompleted
                      ? "text-slate-800"
                      : "text-gray-400"
                  }`}
                >
                  {label}
                </p>
              </div> 
              {index < 4 && (
                <div
                  className={`flex-1 border-t-4 mb-8 -mx-[51px] z-0 ${
                    step > index + 1
                      ? "border-cornflower-blue-500 "
                      : step === index + 1
                      ? "border-cornflower-blue-500 border-dashed"
                      : "border-gray-300 border-dashed"
                  }`}
                ></div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1 - Authentication */}
      {step === 1 && (
        <div className="w-1/3 place-self-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img className="w-40 place-self-center" src={Form1Logo} alt="" />
            <div className="text-center text-slate-900 text-xl font-medium">
              Authentication Key Entry
            </div>
            <span className="text-center text-slate-900 text-base font-normal">
              Input your unique access token in the field below to authenticate
              your identity swiftly and seamlessly.
            </span>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Shopify Store Name
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={storeName}
                placeholder="Enter Shopify store name"
                onChange={(e) => setStoreName(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Shopify Store URL
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={domain}
                placeholder="yourstore.myshopify.com"
                onChange={(e) => setDomain(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                App Client ID
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={appClientId}
                placeholder="Enter App Client ID"
                onChange={(e) => setappClientId(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                App Secret
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={appSecret}
                placeholder="Enter App Secret"
                onChange={(e) => setappSecret(e.target.value)}
              />
            </div>

            <div className="flex gap-2 justify-center mt-4">
              <button
                type="button"
                className="rounded-lg px-6 py-2 bg-white text-cornflower-blue-500 border border-cornflower-blue-500 hover:cornflower-blue-500 text-sm font-medium"
                onClick={() => navigate("/integrations")}
              >
                Cancel
              </button>
              <PrimaryBlueButton
                label="Create Connection"
                onClick={async () => {
                  if (isNextActive) {
                    await handleSubmit();
                  }
                }}
                className={nextBtnClass}
                disabled={!isNextActive || sending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 2 - Permission Request (blank for now) */}
      {step === 2 && (
        <div className="w-2/3 place-self-center text-center relative">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <div className="flex justify-center gap-10">
              <img
                className="w-20 place-self-center"
                src={ShopifyLogo}
                alt=""
              />
              <img className="place-self-center" src={ArrowsLeftRight} alt="" />
              <img className="w-30 place-self-center" src={VelaroLogo} alt="" />
            </div>
            <div>
              <div className="text-center text-slate-900 text-xl font-medium mb-6">
                Integration Permission Request
              </div>
              <div className="text-center text-slate-800 text-md font-medium">
                Velaro wants access to:
              </div>

              <div className="grid grid-cols-2 gap-10 text-left mt-4">
                <div>
                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">
                      Customers Profiles
                    </div>
                    <div className="text-xs">
                      Access customer profiles including name, email, phone, and
                      address to display contact info inside Velaro.
                    </div>
                  </div>
                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Orders</div>
                    <div className="text-xs">
                      Retrieve order history, order status, and payment details
                      so agents can reference purchases during support.
                    </div>
                  </div>
                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Products</div>
                    <div className="text-xs">
                      Sync product catalog data (titles, SKUs, prices) to assist
                      with customer inquiries and recommendations.
                    </div>
                  </div>

                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Discounts</div>
                    <div className="text-xs">
                      Fetch discount codes and promotions to personalize
                      customer offers during chats.
                    </div>
                  </div>
                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Store Settings</div>
                    <div className="text-xs">
                      Read basic store information (store name, currency, time
                      zone) for contextual display.
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Inventory</div>
                    <div className="text-xs">
                      View stock levels and availability to answer customer
                      product questions in real time.
                    </div>
                  </div>

                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Checkouts</div>
                    <div className="text-xs">
                      Track checkout sessions for abandoned cart follow-up and
                      proactive engagement.
                    </div>
                  </div>

                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Fulfillment</div>
                    <div className="text-xs">
                      Access fulfillment details (shipping status, tracking
                      numbers) to provide delivery updates.
                    </div>
                  </div>

                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Draft Orders</div>
                    <div className="text-xs">
                      Allow agents to create draft orders directly from Velaro
                      conversations for faster sales.
                    </div>
                  </div>

                  <div className="text-slay-900 mb-6">
                    <div className="text-base font-medium">Reports</div>
                    <div className="text-xs">
                      Access analytics and reports for deeper insights into
                      sales and customer behavior.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-center">
              <button
                type="button"
                className="rounded-lg px-6 py-2 bg-white text-cornflower-blue-500 border border-cornflower-blue-500 hover:cornflower-blue-500 text-sm font-medium"
                onClick={() => navigate("/integrations")}
              >
                Cancel
              </button>
              <PrimaryBlueButton
                label="Grant Access"
                onClick={handleGrant}
                className={nextBtnClass}
                disabled={false} //{!isNextActive || sending}
              />
            </div>
          </div>

          {isConnecting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 z-50">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-cornflower-blue-500"></div>
              <div className="text-slate-800 text-lg font-medium">
                Connecting to Shopify...
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3 - Connection Success */}
      {step === 3 && (
        <div className="w-2/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img className="w-40 place-self-center" src={SecurityLogo} alt="" />
            <div className="text-center text-slate-800 text-xl font-medium">
              Congratulations! Successful Integration Connection
              <p className="text-center text-slate-800 text-sm font-normal">
                Enjoy a seamless flow of data between Velaro and Shopify. <br />
                Take the next step to tailor your integration by configuring
                settings.
              </p>
            </div>

            <div className="flex gap-2 justify-center">
              <button
                type="submit"
                className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-cornflower-blue-500 text-sm font-medium"
                onClick={() => navigate("/integrations/")}
              >
                Finish Later
              </button>
              <PrimaryBlueButton
                label="Complete to Setup"
                onClick={() => setStep(step + 1)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 4 - Integration Settings */}
      {step === 4 && (
        <div className="">
          <div className="w-full py-8 flex inline-flex justify-between items-center">
            <div>
              <div className="text-slate-800 text-base font-medium">
                Choose which records to sync
              </div>
              <div className="text-slate-500 text-sm font-normal">
                Mappings connect individual fields with matching information
                from each app.
              </div>
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-cornflower-blue-500 text-sm font-medium"
                onClick={() => setStep(step + 1)}
              >
                Done
              </button>
              <PrimaryBlueButton
                label="Add new mapping"
                onClick={addPanel}
                className=""
                prefixIcon={React.createElement(IconPlus)}
              />
            </div>
          </div>

          <div className="mt-4">
            {panels.map((panel) => (
              <ShopifyPanel
                key={panel.id}
                teamId={teamId!}
                scopeType={panel.scopeType}
                showSavedMessage={showSavedMessage}
                isCreateMode={true}
                selectedConfigId={configId}
                isDeleteDisabled={panels.length === 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Step 5 - Integration Complete */}
      {step === 5 && (
        <div className="w-2/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img className="w-40 place-self-center" src={SecurityLogo} alt="" />
            <div className="text-center text-slate-800 text-xl font-medium">
              Congratulations! Integration Completed
            </div>
            <p className="text-center text-slate-800 text-sm font-normal">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. <br />
              Nunc vulputate libero et velit interdum, ac aliquet odio mattis.
            </p>

            <div className="flex gap-2 justify-center">
              <button
                type="button"
                className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-cornflower-blue-500 text-sm font-medium"
                onClick={() => navigate("/integrations/")}
              >
                Back to Dashboard
              </button>
              <PrimaryBlueButton
                label="View Details"
                onClick={() => (window.location.href = "/integrations/shopify")}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ShopifyBackToShopifyOverview() {
  const navigate = useNavigate();
  return (
    <div className="relative flex items-center">
      <div
        className="flex cursor-pointer items-center gap-1 text-slate-600"
        onClick={() => navigate("/integrations")}
      >
        <IconArrowLeft className="h-5 w-5" />
        Back
      </div>
      <span className="absolute left-1/2 transform -translate-x-1/2 font-semibold text-md">
        Integrations
      </span>
    </div>
  );
}
